import { useTracking } from 'react-tracking';
import { push } from 'redux-first-router';

import { useMount } from '@simplywallst/ui-core';

import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import { getPath } from '@/router/utils';

import useUser from '@/hooks/useUser';

import type { CreateNarrativeInput } from '../../hooks/useCreatePublicNarrative';
import { useCreatePublicNarrative } from '../../hooks/useCreatePublicNarrative';
import { validateNarrativeForm } from '../../utils';

import { hasErrors, useNarrativeForm } from '../../hooks/useNarrativeForm';
import { useNarrativeErrorToast } from '../../hooks/useNarrativeErrorToast';
import type { NarrativeForm } from '../../utils/schema';
import { useNarrativeTracking } from '../../hooks/useNarrativeTracking';
import {
  NarrativeFormContent,
  useNarrativeFormHandler,
} from '../NarrativeFormContent';
import {
  narrativeGetValuatorQueryParamInitialValues,
  transformFormValuesToValuation,
} from '../../utils/transformation';
import { useNarrativeBack } from '../../hooks/useNarrativeBack';
import { useEffect, useMemo, useState } from 'react';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { usePastAnnualRevenueEarnings } from '../../hooks/usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';
import loadable from '@loadable/component';
import type { ResolvedQueryData } from '@/utilities/genericTypes';
import type { useNarrativeValuationCompany } from '../../hooks/useNarrativeValuationCompany';
import {
  CreateNarrativeFuturePEMultipleCalculator,
  FuturePEMultipleContext,
  useFuturePEMultipleCalculation,
} from '../Calculators';
import { merge } from 'lodash';
import { useNarrativeInvalidate } from '../../hooks/useNarrativeInvalidate';

export type Props = {
  company: ResolvedQueryData<typeof useNarrativeValuationCompany>['Company'];
  initialUserHoldsPosition: boolean;
};

const NarrativeOnboardingModal = loadable(
  () => import('@components/NarrativeOnboardingModal')
);

export const PublicNarrativeCreate = ({
  company,
  initialUserHoldsPosition,
}: Props) => {
  const { data: user } = useUser();
  const { name, displayName, imageUrl } = user;
  const [showingOnboarding, setShowingOnboarding] = useState(
    !Boolean(user.displayName)
  );

  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'public-narrative',
    userId: user?.id,
    companyId: company.id,
  });
  const { returnPath, goBack } = useNarrativeBack();
  const { invalidate } = useNarrativeInvalidate();

  const { mutate: createNarrative, status: createNarrativeStatus } =
    useCreatePublicNarrative({
      onSuccess: (narrativeId) => {
        const path = getPath({
          type: ROUTE_PUBLIC_NARRATIVE_EDIT,
          payload: { id: narrativeId },
          query: returnPath
            ? {
                company: company.id,
                returnPath,
              }
            : {},
        });
        invalidate();
        trackEvent({
          action: 'save',
        });
        push(path);
        goBack();
      },
    });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  useEffect(() => {
    if (showingOnboarding) {
      trackEvent({ action: 'onboarding' });
    }
  }, [showingOnboarding, trackEvent]);

  useEffect(() => {
    if (!showingOnboarding && !user?.displayName) {
      goBack();
    }
  }, [showingOnboarding, user?.displayName, goBack]);

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
  });

  const strategyContext = useMemo(
    () =>
      new FuturePEMultipleContext(
        new CreateNarrativeFuturePEMultipleCalculator(company)
      ),
    [company]
  );

  const {
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
  } = useFuturePEMultipleCalculation(strategyContext);

  const initialNarrativeForm: NarrativeForm = useMemo(() => {
    const calculatorOutputs = calculateFairValueFromInitialisation();

    return merge(
      {
        ...calculatorOutputs,
        userHoldsPosition: initialUserHoldsPosition,
        title: '',
        content: {
          text: '',
          html: '',
        },
      },
      narrativeGetValuatorQueryParamInitialValues()
    );
  }, [calculateFairValueFromInitialisation, initialUserHoldsPosition]);

  const {
    values,
    setPartialValues,
    errors,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
    resetForm,
  } = useNarrativeForm({
    initialValues: initialNarrativeForm,
    validate: validateNarrativeForm,
  });
  useNarrativeErrorToast({ errors, submitCount });
  useNarrativeTracking({ trackEvent, dirty, errors });

  const handleSubmit = () => {
    setSubmitCount((c) => c + 1);

    if (hasErrors(validateForm())) return;

    if (typeof company !== 'undefined') {
      const { id } = company;
      const currencyIso = getTradingItemCurrencyISO(company?.data.currencyInfo);
      if (!currencyIso) throw new Error('Currency ISO is missing');
      const { content, title, userHoldsPosition } = values;

      const payload: CreateNarrativeInput = {
        companyId: id,
        contentBlocks: content.html
          ? [
              {
                contentHtml: content.html,
              },
            ]
          : [],
        title,
        userHoldsPosition,
        valuations: [
          transformFormValuesToValuation({
            values,
          }),
        ],
      };

      createNarrative({ input: payload, setPrimary: true });
      setSubmitCount(0);
    }
  };

  const isLoading = createNarrativeStatus === 'pending';
  const author = useMemo(
    () => ({
      displayName,
      imageUrl,
      name,
      role: 'Community Contributor',
      url: '',
    }),
    [displayName, imageUrl, name]
  );

  const {
    handleContentChange,
    handleHeaderChange,
    handleSetFairValue,
    handleSetPartialValues,
  } = useNarrativeFormHandler({
    calculateFairValue,
    calculateRevenueGrowthRateFromFairValue,
    setPartialValues,
    values,
  });

  if (showingOnboarding)
    return (
      <Track>
        <NarrativeOnboardingModal
          companyId={company.id}
          isOpen={showingOnboarding}
          onClose={() => {
            setShowingOnboarding(false);
          }}
        />
      </Track>
    );

  return (
    <Track>
      <NarrativeFormContent
        errors={errors}
        values={values}
        onChange={handleSetPartialValues}
        onSetFairValue={handleSetFairValue}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        submitLabel="Save"
        company={company}
        initialContentHtml={initialNarrativeForm.content.html}
        author={author}
        onReset={resetForm}
        pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
        companyId={company.id}
        calculationBreakDownOutput={calculateBreakDown({
          discountRatePercentage: values.discountRatePercentage,
          futurePE: values.priceEarningsMultiple,
          profitMarginPercentage: values.profitMarginPercentage,
          revenueGrowthRatePercentage: values.revenuePercentage,
          timeFrame: values.timePeriod,
        })}
        onContentChange={handleContentChange}
        onHeaderChange={handleHeaderChange}
      />
    </Track>
  );
};
